import React from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../i18n/config';
import { AlertTriangle, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';

interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmButtonText?: string;
  cancelButtonText?: string;
  isDestructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  itemName?: string;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  title,
  message,
  confirmButtonText,
  cancelButtonText,
  isDestructive = true,
  onConfirm,
  onCancel,
  itemName
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';

  return (
    <Dialog open={open} onOpenChange={onCancel}>
      <DialogContent
        className="sm:max-w-[425px]"
        dir={getLanguageDirection(i18n.language)}
      >
        {/* Warning Icon */}
        {isDestructive && (
          <div className="absolute -top-12 left-1/2 -translate-x-1/2 flex items-center justify-center w-12 h-12 rounded-full bg-destructive shadow-lg">
            <AlertTriangle className="w-6 h-6 text-destructive-foreground" />
          </div>
        )}

        {/* Close Button */}
        <button
          onClick={onCancel}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">{t('common.buttons.close')}</span>
        </button>

        <DialogHeader className="pt-6">
          <DialogTitle className="text-center text-xl font-semibold">
            {title}
          </DialogTitle>
        </DialogHeader>

        <Separator />

        <DialogDescription className="text-start py-4">
          {message}
        </DialogDescription>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onCancel}
            className="transition-smooth"
          >
            {cancelButtonText || t('common.buttons.cancel')}
          </Button>
          <div className="w-1"></div>
          <Button
            variant="destructive"
            onClick={onConfirm}
            className="transition-smooth"
          >
            {confirmButtonText || t('common.buttons.delete')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmationDialog;
